import { subscribeByID } from '@adornis/baseql/operations/mongo.js';
import { confirmDialog, guardDialog, showDialog } from '@adornis/dialog/dialog-functions.js';
import { EntityPickerInput } from '@adornis/entity-picker/client/entity-picker-input.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { StringAspect } from '@adornis/filter/aspect-schemas/StringAspect.js';
import { stateful, useObservable, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { hoverTooltip } from '@adornis/popover/hover-tooltip.js';
import { webcomponent } from '@adornis/functional-lit/webcomponent.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { DialogHeader } from '@adornis/components/client/DialogHeader.js';
import { format } from 'date-fns';
import { html, nothing } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import { SgSnackbar } from '../../../client/components/ui/sg-snackbar.js';
import { Permission, Role } from '../../../permissions/roles.js';
import { Person } from '../../../users/entities/person.js';
import { UserListAPI } from '../../../users/list/api/UserListAPI.js';
import { InterviewSlot } from '../entities/InterviewSlot.js';
import type { MappedUITimeSlot } from './sg-interview-slot-administration.js';

const statusBadge = (hasInterviewer: boolean, hasStudent: boolean) => {
  if (hasInterviewer && hasStudent) {
    return html`
      <div class="badge badge-success badge-sm">
        <span class="icon-[material-symbols--check-circle-rounded] size-4"></span>
        Termin vollständig
      </div>
    `;
  }

  if (hasInterviewer) {
    return html`
      <div class="badge badge-warning badge-sm">
        <span class="icon-[material-symbols--pending-rounded] size-4"></span>
        SuS hat diesen Termin noch nicht gebucht
      </div>
    `;
  }

  return html`
    <div class="badge badge-error badge-sm">
      <span class="icon-[material-symbols--pending-rounded] size-4"></span>
      Kein Gesprächspartner zugewiesen
    </div>
  `;
};

const renderSingleInterview = stateful((props: { slot: MappedUITimeSlot; deleteSlot: (_id: string) => void }) => {
  const user = useObservable(() => currentUser(), []);
  const { value: interviewSlot, loading: slotLoading } = useQuery(
    () =>
      subscribeByID(InterviewSlot, props.slot.id)({ _id: 1, startDate: 1, endDate: 1, interviewerID: 1, childID: 1 }),
    [props.slot.id],
  );

  const { value: child, loading: childLoading } = useQuery(
    () =>
      subscribeByID(
        Person,
        interviewSlot?.childID,
      )({ _id: 1, debitorennummer: 1, profilDaten: { stammdaten: { vorname: 1, nachname: 1 } } }),
    [interviewSlot?.childID],
  );

  if (slotLoading || !interviewSlot || childLoading) {
    return html`
      <div class="card p-4">
        <div class="flex items-center justify-center">
          <span class="loading loading-spinner"></span>
        </div>
      </div>
    `;
  }

  const hasInterviewer = !!interviewSlot.interviewerID;
  const hasStudent = !!interviewSlot.childID;

  return html`
    <div class="card p-4 ${!hasInterviewer ? 'border-error border-2' : ''}">
      <div class="flex flex-col gap-4">
        <!-- Time Display -->
        <div class="flex items-center justify-between gap-2">
          <div class="badge badge-primary font-medium">
            <span class="icon-[material-symbols--calendar-clock-rounded] size-4"></span>
            <span>
              ${interviewSlot.startDate ? format(interviewSlot.startDate, 'HH:mm') : ''} -
              ${interviewSlot.endDate ? format(interviewSlot.endDate, 'HH:mm') : ''}
            </span>
          </div>
          ${
            user?.hasPermission(Permission.ADMINISTRATIVE_SUS_INTERVIEWS)
              ? html`<button
                  type="button"
                  class="btn btn-square btn-ghost btn-sm tooltip hover:text-error"
                  aria-label="Termin löschen"
                  data-tip="Termin löschen"
                  ${hoverTooltip()}
                  @click=${async () => {
                    if (!(await confirmDialog('Termin wirklich löschen?'))) return;

                    await guardDialog(() => interviewSlot.remove(), {
                      success: () => {
                        void SgSnackbar.show('Termin erfolgreich gelöscht');
                        if (interviewSlot._id) props.deleteSlot(interviewSlot._id);
                      },
                    });
                  }}
                >
                  <span class="icon-[material-symbols--delete-rounded] size-5"></span>
                </button>`
              : nothing
          }
        </div>

        <!-- Interviewer Section -->
        <div class="grid grid-cols-[1fr_2fr] items-center gap-3">
          <div class="flex gap-2 ">
            <span
              class="icon-[material-symbols--person-rounded] size-5 ${hasInterviewer ? 'text-success' : 'text-error'}"
            ></span>
            <span class="text-sm font-semibold text-base-content/70">Gesprächspartner:</span>
          </div>
          ${EntityPickerInput({
            class: Person,
            filter: new AdornisFilter({
              aspects: [new StringAspect({ fieldName: 'roles.name', value: Role.MITARBEITER, mode: 'equals' })],
            }),
            operation: UserListAPI.subscribe,
            selectionSet: { _id: 1, debitorennummer: 1, profilDaten: { stammdaten: { vorname: 1, nachname: 1 } } },
            placeholder: 'Kein Gesprächspartner zugewiesen',
            placeholderMode: 'hide',
            value: interviewSlot.interviewerID ?? '',
            renderString: person =>
              `${person.debitorennummer ?? '---'} (${person.profilDaten?.stammdaten?.vorname ?? '---'} ${person.profilDaten?.stammdaten?.nachname ?? '---'})`,
            onPicked: async value => {
              interviewSlot.interviewerID = value;
              await interviewSlot.save();
            },
          })}

          <!-- Student Status -->

          <div class="flex gap-2 ">
            <span class="icon-[material-symbols--school-rounded] size-5"></span>
            <span class="text-sm font-semibold text-base-content/70">SuS:</span>
          </div>
          ${
            hasStudent
              ? html`
                  <div
                    class="badge badge-success badge-outline cursor-pointer"
                    @click=${() => {
                      goTo(`/personen/${child?._id}`);
                    }}
                  >
                    <span class="icon-[material-symbols--check-rounded] size-4"></span>
                    Zugewiesen
                  </div>
                `
              : html`
                  <div class="badge badge-ghost">
                    <span class="icon-[material-symbols--person-add-rounded] size-4"></span>
                    Verfügbar
                  </div>
                `
          }
        </div>

        <!-- Status Indicator -->

        <div class="flex justify-between items-center gap-4">
          <div class="text-sm text-base-content/70">Termin-ID: ${interviewSlot._id}</div>
          <div class="flex items-center gap-1">${statusBadge(hasInterviewer, hasStudent)}</div>
        </div>
      </div>
    </div>
  `;
});

const generatorOptionsComponent = webcomponent(
  'sg-interview-detail-dialog',
  (props: { timeSlots: MappedUITimeSlot[]; resolve: () => void }) => {
    const { value: interviewSlots, loading: interviewSlotsLoading } = useQuery(
      () =>
        subscribeByID(
          InterviewSlot,
          props.timeSlots.map(slot => slot.id),
        )({ _id: 1, schoolID: 1, startDate: 1, endDate: 1, childID: 1, interviewerID: 1 }),
      [],
    );

    const [manualUpdate, setManualUpdate] = useState(false);
    const update = () => setManualUpdate(!manualUpdate);

    if (interviewSlotsLoading || !interviewSlots?.length)
      return html`<div class="flex items-center justify-center p-4">
        <span class="loading loading-spinner"></span>
      </div>`;

    return html`
      <div class="flex flex-col p-8 gap-6 max-h-[90vh] overflow-y-auto">
        ${DialogHeader({
          title: html`Termine am ${props.timeSlots[0]?.day} den ${props.timeSlots[0]?.date} um
          ${props.timeSlots[0]?.time}`,
          close: () => props.resolve(),
          closeLabel: 'Dialog schließen',
        })}
        <div class="grid gap-4">
          ${repeat(
            props.timeSlots,
            slot => slot.id,
            slot =>
              renderSingleInterview({
                slot,
                deleteSlot: (id: string) => {
                  props.timeSlots.splice(
                    props.timeSlots.findIndex(slot => slot.id === id),
                    1,
                  );
                  update();
                },
              }),
          )}
        </div>
      </div>
    `;
  },
  {
    shadowRoot: true,
  },
);

export async function sgInterviewDetailDialog(timeSlots: MappedUITimeSlot[]) {
  // ask the user for the specifics
  await showDialog<void>({
    content: resolve => generatorOptionsComponent.render({ resolve, timeSlots }),
  });
}
